import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { AlertCircle } from "@untitledui/icons"
import { ApiError } from "@/lib/api/client"
import {
  activationApi,
  type ActivationView,
} from "@/lib/api/onboarding-activation"
import { BareShell } from "@/components/onboarding-activation/activation-chrome"
import { ActivationFlow } from "@/components/onboarding-activation/activation-flow"

// Public sales-assisted onboarding entry — /activate/:token. No login, no app
// shell — a standalone surface with its own design language. Resolves the
// server-authoritative onboarding session by its opaque token, then hands off
// to the flow. MOCK-ONLY (see onboarding-activation.ts). Try /activate/demo.
export default function ActivatePage() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<ActivationView | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "expired" | "error">("loading")

  useEffect(() => {
    if (!token) return
    let alive = true
    setState("loading")
    activationApi
      .get(token)
      .then((view) => {
        if (!alive) return
        setData(view)
        setState(view.status === "expired" ? "expired" : "ready")
      })
      .catch((err) => {
        if (!alive) return
        if (err instanceof ApiError && err.status === 410) setState("expired")
        else setState("error")
      })
    return () => {
      alive = false
    }
  }, [token])

  if (state === "loading") {
    return (
      <BareShell>
        <div className="grid place-items-center">
          <span className="size-6 animate-spin rounded-full border-2 border-[#DCE4DF] border-t-emerald-600" />
        </div>
      </BareShell>
    )
  }

  if (state !== "ready" || !data) {
    const expired = state === "expired"
    return (
      <BareShell>
        <div className="text-center">
          <div className="mx-auto grid size-12 place-items-center rounded-full bg-amber-50">
            <AlertCircle className="size-6 text-amber-600" aria-hidden />
          </div>
          <h1 className="mt-5 text-[22px] font-bold tracking-tight text-[#0C2018]">
            {expired ? "This setup link has expired" : "This link isn’t active"}
          </h1>
          <p className="mt-2.5 text-[14.5px] leading-relaxed text-[#5A6A61]">
            {expired
              ? "For your security, onboarding links expire after a set period. Reach out to your ETX rep and they’ll send you a fresh one."
              : "The onboarding link you opened isn’t available. Double-check the link from your email, or contact your ETX representative."}
          </p>
        </div>
      </BareShell>
    )
  }

  return <ActivationFlow token={token!} initial={data} />
}
